import { Link } from '@tanstack/react-router';
import { MainLayout } from '../layouts/MainLayout';
import { ExternalLink, Tent, Bell, Code, Heart } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

export function AboutPage() {
  usePageMeta({
    title: 'About | HellaCamping',
    description: 'Learn about HellaCamping, an open data project making public campground information accessible to everyone.',
    canonicalPath: '/about',
  });

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto">
        <div className="mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">About HellaCamping</h1>
          <p className="text-lg text-gray-500">An open data project for public lands</p>
        </div>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Why we exist</h2>
            <p className="text-gray-700 leading-relaxed">
              HellaCamping started as an open data project born out of frustration. When
              the state of California awarded a{' '}
              <a
                href="https://www.sfchronicle.com/news/article/Lawsuit-filed-over-Calif-recreation-reservation-13892789.php"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 hover:text-emerald-700 underline inline-flex items-center gap-1"
              >
                $66 million contract
                <ExternalLink className="w-3.5 h-3.5" />
              </a>{' '}
              to redesign its campground reservation system, the result was a website so
              poorly built that it was{' '}
              <a
                href="https://www.sfchronicle.com/news/article/Lawsuit-filed-over-Calif-recreation-reservation-13892789.php"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 hover:text-emerald-700 underline inline-flex items-center gap-1"
              >
                sued for being inaccessible to the blind
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              , difficult for everyone else to use, and a case study in what happens when
              governments select technology vendors without meaningful accountability.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              This isn't just a California problem. Across the country, public recreation
              data is locked behind clunky government websites with confusing interfaces,
              broken search, and reservation systems that feel like they were designed to
              keep people out rather than get them outdoors.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">What we believe</h2>
            <p className="text-gray-700 leading-relaxed">
              Public lands belong to the public &mdash; and so does the data about them.
              Campground availability, park information, and site details are paid for by
              taxpayers and should be freely accessible in a format that actually works.
              We aggregate data from Recreation.gov, ReserveCalifornia, and dozens of
              state park systems to give you a single, fast, usable interface for finding
              a place to camp.
            </p>
          </section>

          <div className="grid sm:grid-cols-2 gap-4 not-prose">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <Code className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-gray-900">Open data</h3>
              </div>
              <p className="text-sm text-gray-600">
                Core search and availability data is free for everyone. Public land
                information should never be paywalled.
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <Bell className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-gray-900">Funded by features</h3>
              </div>
              <p className="text-sm text-gray-600">
                Advanced features like availability notifications help fund the project
                so we can keep the core experience free.
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <Tent className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-gray-900">For campers</h3>
              </div>
              <p className="text-sm text-gray-600">
                Built by people who actually camp and got tired of fighting reservation
                websites to do it.
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <Heart className="w-5 h-5 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-gray-900">Accessible</h3>
              </div>
              <p className="text-sm text-gray-600">
                If the government won't build accessible tools for public lands, we will.
                Everyone deserves to go camping.
              </p>
            </div>
          </div>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">How it works</h2>
            <p className="text-gray-700 leading-relaxed">
              We continuously scrape and aggregate campground data from federal and state
              park systems across the country. When sites open up, we detect the change
              and can notify you immediately. The search, maps, and park details are free
              to use. Notifications and alerts are a paid feature that keeps the lights on
              and the scrapers running.
            </p>
          </section>

          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6 not-prose">
            <p className="text-emerald-800 text-sm leading-relaxed">
              HellaCamping is not affiliated with any government agency. We link directly
              to official reservation systems so you can book through the original
              provider. We don't sell reservations &mdash; we just make them easier to find.
            </p>
          </div>

          <div className="pt-4 not-prose">
            <Link
              to="/search"
              search={{}}
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
            >
              <Tent className="w-5 h-5" />
              Start searching for campsites
            </Link>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
